import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  getMembershipDashboard,
  getMemberships,
  updateStatus,
  deleteMembership,
  getMembershipLookups,
} from "../api/membershipControlApi";
import MembershipApplications from "../components/control/sections/MembershipApplicationsDirectorySection";
import MembershipRenewals from "../components/control/sections/MembershipRenewalsDirectorySection";
import MembershipDashboardMetricsSection from "../components/control/sections/MembershipDashboardMetricsSection";
import MembershipCreateSection from "../components/control/sections/MembershipCreateSection";
import MembershipDirectorySection, {
  MEMBERSHIP_DIRECTORY_COLUMN_DEFS,
  createInitialMembershipColumnVisibility,
  membershipDirectoryCellValue,
  membershipDefaultDirectoryFilters,
} from "../components/control/sections/MembershipDirectorySection";
import MembershipQuickJumpSection from "../components/control/sections/MembershipQuickJumpSection";
import MembershipMastersSection from "../components/control/sections/membershipMasters";
import { getCsrfToken } from "../../../public/api/authApi";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { GlobalModal } from "../../../global/GlobalModal";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import { useLiveDashboardStats } from "../../../global/hooks/useLiveDashboardStats";
import { useMembershipRole } from "../utils/useMembershipRole";
import { MEMBERSHIPS_CONTROL_SUBMODULE_SLUG } from "../utils/membershipPermissionSlugs";

// Reusing userBuilder styles for admin consistency
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";

function safeErrorText(err, fallback = "Internal server error. Please try again later.") {
  const m = err?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

const DIRECTORY_PAGE_SIZE = 10;

const MembershipControlPage = ({ showMasters: showMastersProp }) => {
  const perms = useMembershipRole(MEMBERSHIPS_CONTROL_SUBMODULE_SLUG);
  const roleSlug = perms.roleSlug;
  const showMasters =
    showMastersProp !== undefined
      ? showMastersProp
      : perms.canManageMasters && roleSlug !== "sub-admin";
  const [statsReloadToken, setStatsReloadToken] = useState(0);
  const [memberships, setMemberships] = useState([]);
  const [loadingMemberships, setLoadingMemberships] = useState(true);
  const [directoryPage, setDirectoryPage] = useState(1);
  const [directoryTotal, setDirectoryTotal] = useState(0);
  const [directoryTotalPages, setDirectoryTotalPages] = useState(1);
  const [draftDirectoryFilters, setDraftDirectoryFilters] = useState(() =>
    membershipDefaultDirectoryFilters(),
  );
  const [appliedDirectoryFilters, setAppliedDirectoryFilters] = useState(() =>
    membershipDefaultDirectoryFilters(),
  );
  const [lookups, setLookups] = useState({
    membershipTypes: [],
    durationTypes: [],
    feeTypes: [],
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeQuickJump, setActiveQuickJump] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmType, setConfirmType] = useState(null); // 'activate' | 'deactivate' | 'delete'
  const [membershipToHandle, setMembershipToHandle] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [lookupsVersion, setLookupsVersion] = useState(0);
  const [editingMembership, setEditingMembership] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState(
    createInitialMembershipColumnVisibility
  );
  const bumpMasterLookups = useCallback(() => {
    setLookupsVersion((v) => v + 1);
  }, []);

  const getMembershipExportData = useCallback(() => {
    const visibleCols = MEMBERSHIP_DIRECTORY_COLUMN_DEFS.filter(
      (c) => columnVisibility[c.id]
    );
    const headers = visibleCols.map((c) => c.label);
    const rows = memberships.map((m) =>
      visibleCols.map((c) => membershipDirectoryCellValue(c.id, m))
    );
    return { headers, rows };
  }, [memberships, columnVisibility]);

  const { dashboardStats, loadingStats, refreshStats } = useLiveDashboardStats({
    fetcher: getMembershipDashboard,
    selectData: (data) => data?.data ?? null,
    refreshDeps: [statsReloadToken],
  });

  const directoryQueryParams = useMemo(() => {
    const q = {
      page: directoryPage,
      limit: DIRECTORY_PAGE_SIZE,
    };
    const plan = String(appliedDirectoryFilters.plan_search || "").trim();
    if (plan) q.name = plan;

    const col = appliedDirectoryFilters.column;
    const val = String(appliedDirectoryFilters.value || "").trim();
    if (col && val) {
      if (col === "membership_name") q.name_exact = val;
      if (col === "type_name") {
        const t = (lookups.membershipTypes || []).find((x) => String(x.type_name) === val);
        if (t?.id != null) q.membership_type_id = String(t.id);
      }
      if (col === "duration_name") {
        const d = (lookups.durationTypes || []).find((x) => String(x.duration_name) === val);
        if (d?.id != null) q.duration_type_id = String(d.id);
      }
      if (col === "fee_type_name") {
        const f = (lookups.feeTypes || []).find((x) => String(x.fee_type_name) === val);
        if (f?.id != null) q.fee_type_id = String(f.id);
      }
      if (col === "status_name") {
        if (val.toLowerCase() === "active") q.is_active = "1";
        else if (val.toLowerCase() === "inactive") q.is_active = "2";
      }
      if (col === "price") {
        const n = Number(String(val).replace(/[^\d.-]/g, ""));
        if (Number.isFinite(n)) q.price_match = String(n);
      }
      if (col === "registration_fee") {
        const n = Number(String(val).replace(/[^\d.-]/g, ""));
        if (Number.isFinite(n)) q.registration_fee_match = String(n);
      }
      if (col === "discount") {
        const n = Number(String(val).replace(/[^\d.-]/g, ""));
        if (Number.isFinite(n)) q.discount_match = String(n);
      }
      if (col === "max_students_allowed") {
        const n = parseInt(String(val).replace(/\D/g, ""), 10);
        if (Number.isFinite(n)) q.max_students_allowed_match = String(n);
      }
      if (col === "total_students_registered") {
        const n = parseInt(String(val).replace(/\D/g, ""), 10);
        if (Number.isFinite(n))
          q.total_students_registered_match = String(n);
      }
      if (col === "total_amount_paid") {
        const n = Number(String(val).replace(/[^\d.-]/g, ""));
        if (Number.isFinite(n)) q.total_amount_paid_match = String(n);
      }
      if (col === "description") q.description_contains = val;
      if (col === "benefits") q.benefits_contains = val;
    }
    return q;
  }, [directoryPage, appliedDirectoryFilters, lookups]);

  const fetchMembershipsList = useCallback(async () => {
    setLoadingMemberships(true);
    setErrorMsg("");
    try {
      const data = await getMemberships(directoryQueryParams);
      setMemberships(data?.data || []);
      const meta = data?.meta || {};
      setDirectoryTotal(Number(meta.total || 0));
      setDirectoryTotalPages(Math.max(1, Number(meta.totalPages || 1)));
      setDirectoryPage(Number(meta.page || 1));
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setLoadingMemberships(false);
    }
  }, [directoryQueryParams]);

  const fetchLookups = useCallback(async () => {
    try {
      const data = await getMembershipLookups();
      if (data?.success && data?.data) {
        setLookups({
          membershipTypes: data.data.membershipTypes || [],
          durationTypes: data.data.durationTypes || [],
          feeTypes: data.data.feeTypes || [],
        });
      }
    } catch (err) {
      console.error("Failed to fetch lookups:", err);
    }
  }, []);

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups, lookupsVersion]);

  useEffect(() => {
    const t = setTimeout(() => setAppliedDirectoryFilters({ ...draftDirectoryFilters }), 350);
    return () => clearTimeout(t);
  }, [draftDirectoryFilters]);

  useEffect(() => {
    setDirectoryPage(1);
  }, [appliedDirectoryFilters]);

  useEffect(() => {
    fetchMembershipsList();
  }, [fetchMembershipsList]);

  const handleMembershipFormSuccess = (msg) => {
    setSuccessMsg(msg);
    setErrorMsg("");
    setEditingMembership(null);
    setStatsReloadToken((t) => t + 1);
    refreshStats();
    fetchMembershipsList();
  };

  const handleRequestEditMembership = (row) => {
    setEditingMembership(row);
    requestAnimationFrame(() => {
      document
        .getElementById("ms-create")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleActivate = (id) => {
    const membership = memberships.find((m) => m.id === id);
    setMembershipToHandle(membership);
    setConfirmType("activate");
    setShowConfirmModal(true);
  };

  const handleDeactivate = (id) => {
    const membership = memberships.find((m) => m.id === id);
    setMembershipToHandle(membership);
    setConfirmType("deactivate");
    setShowConfirmModal(true);
  };

  const handleDelete = (id) => {
    const membership = memberships.find((m) => m.id === id);
    setMembershipToHandle(membership);
    setConfirmType("delete");
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (!membershipToHandle || !confirmType) return;
    setIsConfirming(true);
    setErrorMsg("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      if (confirmType === "activate") {
        await updateStatus(membershipToHandle.id, 1, csrfToken);
        setSuccessMsg("Membership activated successfully.");
      } else if (confirmType === "deactivate") {
        await updateStatus(membershipToHandle.id, 2, csrfToken);
        setSuccessMsg("Membership deactivated successfully.");
      } else if (confirmType === "delete") {
        await deleteMembership(membershipToHandle.id, csrfToken);
        setSuccessMsg("Membership deleted successfully.");
      }

      setShowConfirmModal(false);
      setMembershipToHandle(null);
      setConfirmType(null);
      setStatsReloadToken((t) => t + 1);
    refreshStats();
      fetchMembershipsList();
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setIsConfirming(false);
    }
  };

  const QUICK_JUMP_ITEMS = [
    { key: "stats", label: "Performance overview", targetId: "ms-stats" },
    { key: "applications", label: "Applications", targetId: "ms-applications" },
    { key: "renewals", label: "Renewals", targetId: "ms-renewals" },
    ...(perms.canCreate || perms.canEdit
      ? [{ key: "create", label: "Create Membership", targetId: "ms-create" }]
      : []),
    { key: "list", label: "Directory", targetId: "ms-list" },
    ...(showMasters
      ? [{ key: "masters", label: "Master tables", targetId: "ms-masters" }]
      : []),
  ];

  if (perms.permissionsLoading) {
    return (
      <div className="user-builder-shell">
        <p className="user-builder-panel__hint">Loading permissions…</p>
      </div>
    );
  }

  if (!perms.canView) {
    return (
      <div className="user-builder-shell">
        <header className="user-builder-hero">
          <div className="user-builder-hero__text">
            <h2 className="user-builder-hero__title">Membership Management</h2>
            <p className="user-builder-hero__sub">
              You do not have view access to this screen.
            </p>
          </div>
        </header>
      </div>
    );
  }

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Administrative Area</p>
          <h2 className="user-builder-hero__title">Membership Management</h2>
          <p className="user-builder-hero__sub">
            Manage membership plans, pricing, and availability. Configure master
            data for types, durations, and fee structures.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <GlobalFeedback type="error" text={errorMsg} />

      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          <MembershipDashboardMetricsSection
            dashboardStats={dashboardStats}
            loadingStats={loadingStats}
          />

          <MembershipApplications
            canEdit={perms.canEdit}
            canDelete={perms.canDelete}
          />

          <MembershipRenewals
            canEdit={perms.canEdit}
            canDelete={perms.canDelete}
          />

          <MembershipCreateSection
            lookupsRefreshKey={lookupsVersion}
            editingMembership={editingMembership}
            onCancelEdit={() => setEditingMembership(null)}
            onSuccess={handleMembershipFormSuccess}
            canCreate={perms.canCreate}
            canEdit={perms.canEdit}
          />

          <MembershipDirectorySection
            memberships={memberships}
            draftDirectoryFilters={draftDirectoryFilters}
            onDraftDirectoryFiltersChange={setDraftDirectoryFilters}
            onClearDirectoryFilters={() =>
              setDraftDirectoryFilters(membershipDefaultDirectoryFilters())
            }
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            getMembershipExportData={getMembershipExportData}
            loadingMemberships={loadingMemberships}
            directoryPage={directoryPage}
            directoryTotalPages={directoryTotalPages}
            directoryTotal={directoryTotal}
            directoryPageSize={DIRECTORY_PAGE_SIZE}
            onDirectoryPageChange={setDirectoryPage}
            onActivate={handleActivate}
            onDeactivate={handleDeactivate}
            onDelete={handleDelete}
            onRequestEditMembership={handleRequestEditMembership}
            canEdit={perms.canEdit}
            canDelete={perms.canDelete}
          />

          <MembershipMastersSection
            showMasters={showMasters}
            onDirectoryChange={bumpMasterLookups}
            canCreate={perms.canCreate}
            canEdit={perms.canEdit}
            canDelete={perms.canDeleteMasters}
          />
        </div>

        <MembershipQuickJumpSection
          asideAriaLabel="Dashboard sections"
          items={QUICK_JUMP_ITEMS}
          activeKey={activeQuickJump}
          onActiveChange={setActiveQuickJump}
        />
      </div>

      <GlobalModal
        open={showConfirmModal}
        onClose={() => !isConfirming && setShowConfirmModal(false)}
        title={
          confirmType === "activate"
            ? "Confirm Activation"
            : confirmType === "deactivate"
              ? "Confirm Deactivation"
              : "Confirm Deletion"
        }
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton
              onClick={() => setShowConfirmModal(false)}
              disabled={isConfirming}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant={
                confirmType === "delete" || confirmType === "deactivate"
                  ? "danger"
                  : "primary"
              }
              onClick={handleConfirmAction}
              loading={isConfirming}
            >
              Confirm{" "}
              {confirmType?.charAt(0).toUpperCase() + confirmType?.slice(1)}
            </GlobalButton>
          </div>
        }
      >
        <p>
          Are you sure you want to <b>{confirmType}</b> the membership{" "}
          <b>{membershipToHandle?.membership_name}</b>?
        </p>
        {confirmType === "delete" && (
          <p
            style={{
              marginTop: "0.5rem",
              fontSize: "0.875rem",
              color: "#dc2626",
            }}
          >
            This action cannot be undone.
          </p>
        )}
      </GlobalModal>
    </div>
  );
};

export default MembershipControlPage;
